import { useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useBrainStore } from '@/state/brainStore';
import { useUIStore } from '@/state/uiStore';
import { useSimulationStore } from '@/state/simulationStore';
import { customerName, aud } from '@/utils/format';
import type { Deposit } from '@/domain/types';
import { X, Search, Check, Banknote } from 'lucide-react';
import clsx from 'clsx';

interface Props {
  open: boolean;
  onClose: () => void;
}

const METHODS: Array<{ key: NonNullable<Deposit['method']>; label: string }> = [
  { key: 'eftpos', label: 'EFTPOS' },
  { key: 'cash', label: 'Cash' },
  { key: 'bank-transfer', label: 'Bank transfer' },
];

/**
 * Logs a deposit that was taken in person (the studios do this in Excel today —
 * CU 86d3eerqn). Creates a paid Deposit with stripePaymentLinkId 'manual' and a
 * payment method. No Stripe fees on a manual deposit.
 */
export default function ManualDepositModal({ open, onClose }: Props) {
  const customers = useBrainStore((s) => s.customers);
  const upsertDeposit = useBrainStore((s) => s.upsertDeposit);
  const appendActivity = useBrainStore((s) => s.appendActivity);
  const incStat = useBrainStore((s) => s.incStat);
  const pushNotification = useUIStore((s) => s.pushNotification);
  const clock = useSimulationStore((s) => s.clockIso);

  const heroes = Object.values(customers).filter((c) => c.hero);
  const [customerId, setCustomerId] = useState(heroes[0]?.id ?? '');
  const [customerQuery, setCustomerQuery] = useState('');
  const [customerOpen, setCustomerOpen] = useState(false);
  const [amount, setAmount] = useState('200');
  const [method, setMethod] = useState<NonNullable<Deposit['method']>>('eftpos');
  const [date, setDate] = useState(clock.slice(0, 10));

  const searchResults = useMemo(() => {
    const q = customerQuery.trim().toLowerCase();
    const list = Object.values(customers);
    const ranked = q
      ? list.filter((c) =>
          `${c.firstName} ${c.lastName} ${c.phone} ${c.suburb}`.toLowerCase().includes(q),
        )
      : list.filter((c) => c.hero);
    return ranked.slice(0, 12);
  }, [customers, customerQuery]);

  const customer = customerId ? customers[customerId] : undefined;
  const amountNum = Math.max(0, Math.round(Number(amount) || 0));
  const canSave = !!customer && amountNum > 0;

  const save = () => {
    if (!customer) return;
    const id = `dep-manual-${Date.now()}`;
    const paidAtIso = `${date}T12:00:00.000+10:00`;
    const deposit: Deposit = {
      id,
      customerId: customer.id,
      studioId: customer.primaryStudioId,
      amountAud: amountNum,
      amountPaid: amountNum,
      feesAud: 0,
      netAud: amountNum,
      status: 'paid',
      channelSent: 'walk-in',
      stripePaymentLinkId: 'manual',
      method,
      expiresAt: paidAtIso,
      createdAt: paidAtIso,
      paidAt: paidAtIso,
      policy: 'seven-day-rule',
      source: 'walk-in',
    };
    upsertDeposit(deposit);
    incStat('deposits');
    appendActivity({
      id: `act-${id}`,
      type: 'deposit-paid',
      customerId: customer.id,
      studioId: customer.primaryStudioId,
      amount: amountNum,
      body: `Manual deposit ${aud(amountNum)} logged for ${customerName(customer)} (${METHODS.find((m) => m.key === method)?.label}).`,
      timestamp: clock,
    });
    pushNotification({
      intensity: 'high',
      title: 'Deposit logged',
      body: `${aud(amountNum)} · ${METHODS.find((m) => m.key === method)?.label} · ${customerName(customer)}`,
      ttlMs: 4500,
      customerId: customer.id,
    });
    onClose();
  };

  return (
    <AnimatePresence>
      {open ? (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 bg-overlay/80 backdrop-blur-sm flex items-center justify-center px-6"
          onClick={onClose}
        >
          <motion.div
            initial={{ y: 8, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 8, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="bg-ink-deep border border-gold-dim/40 rounded-xl w-full max-w-md p-6 relative"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-label="Log a manual deposit"
            data-testid="manual-deposit-modal"
          >
            <button
              type="button"
              onClick={onClose}
              className="absolute top-3 right-3 text-cream-soft hover:text-cream"
              aria-label="Close"
            >
              <X size={16} />
            </button>
            <p className="label-small-caps text-gold-bright">Log a deposit</p>
            <h2 className="heading-display text-2xl text-cream mt-2 flex items-center gap-2">
              <Banknote size={20} className="text-gold-bright" /> In-person payment
            </h2>

            <div className="block mt-5">
              <span className="label-small-caps">Customer</span>
              {customer && !customerOpen ? (
                <button
                  type="button"
                  onClick={() => {
                    setCustomerOpen(true);
                    setCustomerQuery('');
                  }}
                  className="mt-2 w-full bg-ink-black border border-gold-dim/40 rounded-md px-3 py-2 text-left flex items-center justify-between hover:border-gold-bright/60 transition"
                  data-testid="manual-deposit-customer-selected"
                >
                  <span className="font-inter text-sm text-cream">{customerName(customer)}</span>
                  <span className="font-inter text-[10px] text-cream-faint uppercase tracking-widest">
                    Change
                  </span>
                </button>
              ) : (
                <div className="relative mt-2">
                  <Search
                    size={12}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-cream-faint"
                  />
                  <input
                    autoFocus
                    type="text"
                    value={customerQuery}
                    onChange={(e) => setCustomerQuery(e.target.value)}
                    placeholder="Search name, phone, or suburb"
                    className="w-full bg-ink-black border border-gold-dim/40 rounded-md pl-8 pr-3 py-2 text-sm text-cream focus:outline-none focus:border-gold-bright"
                    data-testid="manual-deposit-customer-search"
                  />
                  <ul className="absolute z-10 mt-1 w-full max-h-64 overflow-y-auto panel-dark py-1 shadow-ink-soft">
                    {searchResults.length === 0 ? (
                      <li className="px-3 py-2 font-serif text-sm text-cream-soft">
                        No matches.
                      </li>
                    ) : null}
                    {searchResults.map((c) => (
                      <li key={c.id}>
                        <button
                          type="button"
                          onClick={() => {
                            setCustomerId(c.id);
                            setCustomerOpen(false);
                          }}
                          className={clsx(
                            'w-full text-left px-3 py-2 text-sm flex items-center justify-between hover:bg-ink-soft/60 transition',
                            c.id === customerId ? 'text-gold-bright' : 'text-cream',
                          )}
                          data-testid={`manual-deposit-customer-option-${c.id}`}
                        >
                          <span className="flex flex-col">
                            <span className="font-inter text-sm">{customerName(c)}</span>
                            <span className="font-inter text-[10px] text-cream-soft uppercase tracking-widest">
                              {c.phone} · {c.suburb}
                            </span>
                          </span>
                          {c.id === customerId ? <Check size={12} /> : null}
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            <label className="block mt-3">
              <span className="label-small-caps">Amount (AUD)</span>
              <input
                type="number"
                min={0}
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="mt-2 w-full bg-ink-black border border-gold-dim/40 rounded-md px-3 py-2 text-sm text-cream focus:outline-none focus:border-gold-bright"
                data-testid="manual-deposit-amount"
              />
            </label>

            <div className="block mt-3">
              <span className="label-small-caps">Method</span>
              <div className="mt-2 grid grid-cols-3 gap-2">
                {METHODS.map((m) => (
                  <button
                    key={m.key}
                    type="button"
                    onClick={() => setMethod(m.key)}
                    className={
                      method === m.key
                        ? 'rounded-md border border-gold-bright bg-gold/10 px-3 py-2 text-xs text-gold-bright'
                        : 'rounded-md border border-gold-dim/30 px-3 py-2 text-xs text-cream-soft hover:border-gold-bright/60'
                    }
                    data-testid={`manual-deposit-method-${m.key}`}
                  >
                    {m.label}
                  </button>
                ))}
              </div>
            </div>

            <label className="block mt-3">
              <span className="label-small-caps">Date</span>
              <input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className="mt-2 w-full bg-ink-black border border-gold-dim/40 rounded-md px-3 py-2 text-sm text-cream focus:outline-none focus:border-gold-bright"
                data-testid="manual-deposit-date"
              />
            </label>

            <div className="flex gap-2 mt-5 justify-end">
              <button type="button" onClick={onClose} className="btn-ghost text-xs">
                Cancel
              </button>
              <button
                type="button"
                onClick={save}
                disabled={!canSave}
                className="btn-primary text-xs disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="manual-deposit-save"
              >
                <Banknote size={12} /> Log deposit
              </button>
            </div>
          </motion.div>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
}
